import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { EndPoints } from "../../axios/endPoints";
import { axiosInstance } from "../../axios/axiosHandler";
import { authHeader } from "../auth-slice/auth-slice";

// Async thunk to upload a training dataset
export const UploadFile = createAsyncThunk(
  "UploadFile",
  async (formData: FormData, thunkAPI) => {
    try {
      // Send a POST request to upload the training dataset
      const response = await axiosInstance.post(
        EndPoints.ML_CREATOR_ADD_TRAINING_DATASET,
        formData,
        authHeader()
      );
      return await response.data;
    } catch (error) {
      // Handle failure by rejecting the action
      return thunkAPI.rejectWithValue(null);
    }
  }
);

// Async thunk to delete a training dataset by its ID
export const DeleteTrainingDataset = createAsyncThunk(
  "DeleteTrainingDataset",
  async (id: number, thunkAPI) => {
    try {
      // Send a POST request to delete the dataset
      const response = await axiosInstance.post(
        EndPoints.ML_CREATOR_DELETE_TRAINING_DATASET + `?id=${id}`,
        null,
        authHeader()
      );
      return await response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

// Async thunk to fetch details of a specific training dataset by its ID
export const GetTrainingDatasetDetails = createAsyncThunk(
  "GetTrainingDatasetDetails",
  async (id: number, thunkAPI) => {
    try {
      // Send a GET request to fetch dataset details
      const response = await axiosInstance.get(
        EndPoints.ML_CREATOR_GET_TRAINING_FILE_DETAILS + `?id=${id}`,
        authHeader()
      );
      return await response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

// Async thunk to fetch details of a specific training dataset by its ID
export const GetDatasetsCosmosDB = createAsyncThunk(
  "GetDatasetsCosmosDB",
  async ({ datasetId, pageNumber, pageSize }: {datasetId:number, pageNumber: number, pageSize: number} , thunkAPI) => {
    try {
      // Send a GET request to fetch dataset details
      const response = await axiosInstance.get(
        EndPoints.ML_CREATOR_GET_TRAINING_FILE_DATA + `?datasetId=${datasetId}&&pageNumber=${pageNumber}&&pageSize=${pageSize}`,
        authHeader()
      );
      return await response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

// Async thunk to fetch details of a specific training model by its ID
export const GetTrainingModelDetails = createAsyncThunk(
  "GetTrainingModelDetails",
  async (id: number, thunkAPI) => {
    try {
      // Send a GET request to fetch model details
      const response = await axiosInstance.get(
        EndPoints.ML_CREATOR_GET_TRAINING_MODEL_DETAILS + `?id=${id}`,
        authHeader()
      );
      return await response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

// Async thunk to delete a training model by its ID
export const DeleteTrainingModel = createAsyncThunk(
  "DeleteTrainingModel",
  async (id: number, thunkAPI) => {
    try {
      // Send a POST request to delete the training model
      const response = await axiosInstance.post(
        EndPoints.ML_CREATOR_DELETE_TRAINING_MODEL + `?id=${id}`,
        null,
        authHeader()
      );
      return await response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

// Async thunk to initiate the training of a model using AI Orchestrator
export const AIOrchestratorInitTrain = createAsyncThunk(
  "AIOrchestratorInitTrain",
  async (body: any, thunkAPI) => {
    try {
      // Send a POST request to initiate training
      const response = await axiosInstance.post(
        EndPoints.ML_CREATOR_AI_ORCHESTRATOR_INIT_TRAIN,
        body,
        authHeader()
      );
      return await response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

// Async thunk to initiate the training of a model using AI Orchestrator
export const GetModelAlgorithmDetails = createAsyncThunk(
  "GetModelAlgorithmDetails",
  async (body: any, thunkAPI) => {
    try {
      // Send a POST request to initiate training
      const response = await axiosInstance.post(
        EndPoints.ML_CREATOR_AI_MODEL_ALGORITHM_LIST,
        body,
        authHeader()
      );
      return await response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

// Create a Redux slice to manage ML creator-related state
export const mlCreatorSlice = createSlice({
  name: "mlcreator",
  initialState: {
    // Define initial state properties
    refreshMLCreatorTable: 0,
    openImportTrainingDataDialog: false,
    chosenFile: null,
    chosenName: "",
    uploadingFile: false,
    deleteFilePopupOpen: false,
    deleteFileBodyParams: null,
    deletingFile: false,
    fileDetailsPopupOpen: false,
    fileDetailsBodyParams: null,
    fileDetailsLoading: false,
    fileDetails: null as any,
    fileDatasetPopupOpen: false,
    fileDatasetBodyParams: null,
    fileDatasetLoading: false,
    fileDataset: null as any,
    selectedCreatorRow: null,
    selectedModelRow: null,
    refreshSelectedMLCreatorTable: 0,
    trainNewModelDialogOpen: false,
    trainNewModelAlgorithmDialogOpen: false,
    trainNewModelAlgorithmDialogLoading: false,
    recommendedModelType: null as any,
    modelAlgorithmList: {} as any,
    trainNewModelLoading: false,
    selectedColumn: null,
    selectedInputColumn: null,
    selectedOutputColumn: null,
    columns: [] as any[],
    inputColumns: [] as any[],
    outputColumns: [] as any[],
    successfulTrainResponseModalOpen: false,
    successfulTrainReponse: null,
    trainingModelDetailsDialogOpen: false,
    trainingModelDialogBodyProps: null,
    trainingModelDetails: null,
    trainingModelDetailsLoading: false,
    deleteTraingModelDialogOpen: false,
    deleteTrainingModelBodyParams: null,
    deletingTrainingModel: false,
    aiInitiateTraining: false,
    failedTrainingDialog: { isOpen: false, body: null },
  },
  reducers: {
    // Toggles the import training data dialog and resets related states
    handleToggleImportTrainingDataDialog: (state, action) => {
      state.openImportTrainingDataDialog = action.payload;
      state.chosenFile = null;
      state.chosenName = "";
    },
    // Sets the chosen file for upload
    handleSetChosenFile: (state, action) => {
      state.chosenFile = action.payload;
    },
    // Sets the chosen file name
    handleSetChosenName: (state, action) => {
      state.chosenName = action.payload;
    },
    // Toggles the delete file popup visibility and sets the body parameters
    handleToggleDeleteFilePopup: (state, action) => {
      state.deleteFilePopupOpen = action.payload.open;
      state.deleteFileBodyParams = action.payload.body;
    },
    // Toggles the file details popup visibility and clears details on close
    handleToggleFileDetailsPopup: (state, action) => {
      state.fileDetailsPopupOpen = action.payload.open;
      state.fileDetailsBodyParams = action.payload.body;
      if (!action.payload.open) {
        state.fileDetails = null;
        state.columns = [];
      }
    },
    // Toggles the file data popup visibility and clears details on close
    handleToggleFileDatasetPopup: (state, action) => {
      state.fileDatasetPopupOpen = action.payload.open;
      state.fileDatasetBodyParams = action.payload.body;
      if (!action.payload.open) {
        state.fileDataset = null;
        state.columns = [];
      }
    },
    // Updates the selected row in the creator table
    handleCreatorRowClick: (state, action) => {
      state.selectedCreatorRow = action.payload;
    },
    // Updates the selected row in the creator table
    handleModelRowClick: (state, action) => {
      state.selectedModelRow = action.payload;
    },
    // Handles opening/closing the train new model dialog and resets states
    handleTrainNewModel: (state, action) => {
      state.columns = [];
      state.trainNewModelDialogOpen = action.payload.isOpen;
      state.selectedColumn = null;
      state.selectedInputColumn = null;
      state.selectedOutputColumn = null;
      state.inputColumns = [];
      state.outputColumns = [];
    },
    // Handles opening/closing the train new model dialog and resets states
    handleTrainNewModelAlgorithmList: (state, action) => {
      state.trainNewModelAlgorithmDialogOpen = action.payload.isOpen;
      state.recommendedModelType = action.payload.selectedModelType;
      state.modelAlgorithmList = action.payload.modelAlgorithmList;
    },
    // Selects a column
    handleSelectColumn: (state, action) => {
      state.selectedColumn = action.payload;
      state.selectedInputColumn = null;
      state.selectedOutputColumn = null;
    },
    // Selects an input column
    handleSelectInputColumn: (state, action) => {
      state.selectedInputColumn = action.payload;
      state.selectedColumn = null;
      state.selectedOutputColumn = null;
    },
    // Selects an output column
    handleSelectOutputColumn: (state, action) => {
      state.selectedOutputColumn = action.payload;
      state.selectedColumn = null;
      state.selectedInputColumn = null;
    },
    // Moves a column to the input columns
    handleMoveColumnToInputLeft: (state, action) => {
      state.inputColumns.push({ ...action.payload });
      state.columns = state.columns.filter(
        (el: any) => el.id !== action.payload.id
      );
      state.selectedColumn = null;
    },
    // Moves a column back to available columns
    handleMoveColumnToColumnsRight: (state, action) => {
      state.columns.push({ ...action.payload });
      state.inputColumns = state.inputColumns.filter(
        (el) => el.id !== action.payload.id
      );
      state.selectedInputColumn = null;
    },
    // Moves a column to the output columns
    handleMoveColumnToOutputRight: (state, action) => {
      state.outputColumns.push({ ...action.payload });
      state.columns = state.columns.filter(
        (el: any) => el.id !== action.payload.id
      );
      state.selectedColumn = null;
    },
    // Moves a column back to available columns from output columns
    handleMoveColumnToColumnsLeft: (state, action) => {
      state.columns.push({ ...action.payload });
      state.outputColumns = state.outputColumns.filter(
        (el) => el.id !== action.payload.id
      );
      state.selectedOutputColumn = null;
    },
    // Moves all columns to input columns
    handleMoveAllToInputColumns: (state) => {
      const columns = [...state.columns];
      state.inputColumns = state.inputColumns.concat(columns);
      state.columns = [];
      state.selectedColumn = null;
    },
    // Moves all columns to output columns
    handleMoveAllToOutputColumns: (state) => {
      const columns = [...state.columns];
      state.outputColumns = state.outputColumns.concat(columns);
      state.columns = [];
      state.selectedColumn = null;
    },
    // Moves all input columns back to available columns
    handleMoveAllToColumnsFromInput: (state) => {
      const inputColumns = [...state.inputColumns];
      state.columns = state.columns.concat(inputColumns);
      state.inputColumns = [];
      state.selectedInputColumn = null;
    },
    // Moves all output columns back to available columns
    handleMoveAllToColumnsFromOutput: (state) => {
      const outputColumns = [...state.outputColumns];
      state.columns = state.columns.concat(outputColumns);
      state.outputColumns = [];
      state.selectedOutputColumn = null;
    },
    // Closes the successful training details modal and refreshes tables
    handleCloseSuccessfulTrainingDetails: (state) => {
      state.successfulTrainResponseModalOpen = false;
      state.refreshMLCreatorTable++;
      state.refreshSelectedMLCreatorTable++;
      state.successfulTrainReponse = null;
    },
    // Toggles the training model details dialog visibility
    handleToggleTrainingModelDetailsDialog: (
      state,
      action: PayloadAction<any>
    ) => {
      state.trainingModelDetailsDialogOpen = action.payload.isOpen;
      state.trainingModelDialogBodyProps = action.payload.body;
      if (!action.payload.isOpen) {
        state.trainingModelDetails = null;
      }
    },
    // Toggles the delete training model dialog visibility
    handleToggleDeleteTrainingModel: (state, action: PayloadAction<any>) => {
      state.deleteTraingModelDialogOpen = action.payload.isOpen;
      state.deleteTrainingModelBodyParams = action.payload.body;
    },
    // Toggles the failed training modal visibility
    handleToggleFailedTrainingModal: (state, action) => {
      state.failedTrainingDialog = action.payload;
    },
    // Refreshes the main ML creator table
    handleRefreshMLCreatorTable: (state) => {
      state.refreshMLCreatorTable++;
    },
    // Refreshes the selected ML creator table
    handleRefreshSelectedMLCreatorTable: (state) => {
      state.refreshSelectedMLCreatorTable++;
    },
  },
  extraReducers: (builder) => {
    // Handle async thunk cases to manage state transitions
    builder.addCase(UploadFile.pending, (state) => {
      state.uploadingFile = true;
    });
    builder.addCase(UploadFile.fulfilled, (state, action) => {
      state.uploadingFile = false;
      if (action.payload.success) {
        state.openImportTrainingDataDialog = false;
        state.refreshMLCreatorTable++;
      }
    });
    builder.addCase(UploadFile.rejected, (state) => {
      state.uploadingFile = false;
    });

    builder.addCase(DeleteTrainingDataset.pending, (state) => {
      state.deletingFile = true;
    });
    builder.addCase(DeleteTrainingDataset.fulfilled, (state, action) => {
      state.deletingFile = false;
      state.deleteFilePopupOpen = false;
      state.deleteFileBodyParams = null;
      if(action && action.payload?.success) {
        state.selectedCreatorRow = null;
        state.refreshMLCreatorTable++;
      }
    });
    builder.addCase(DeleteTrainingDataset.rejected, (state) => {
      state.deletingFile = false;
      state.deleteFilePopupOpen = false;
      state.deleteFileBodyParams = null;
      state.refreshMLCreatorTable++;
    });

    builder.addCase(GetTrainingDatasetDetails.pending, (state) => {
      state.fileDetailsLoading = true;
    });
    builder.addCase(GetTrainingDatasetDetails.fulfilled, (state, action) => {
      state.fileDetailsLoading = false;
      if (action.payload.success) {
        const results = action.payload.result;
        const columns = results.columns.map((el: any, index: number) => ({
          ...el,
          id: index + 1,
        }));
        state.fileDetails = { ...results, columns };
        state.columns = columns;
      }
    });
    builder.addCase(GetTrainingDatasetDetails.rejected, (state) => {
      state.fileDetailsLoading = false;
      state.fileDetails = null;
      state.columns = [];
    });

    
    builder.addCase(GetDatasetsCosmosDB.pending, (state) => {
        state.fileDatasetLoading = true;
    });
    builder.addCase(GetDatasetsCosmosDB.fulfilled, (state, action) => {
      state.fileDatasetLoading = false;
      if (action.payload.success) {
        state.fileDataset = action.payload.result;
      } else {
        state.fileDataset = null;
      }
    });
    builder.addCase(GetDatasetsCosmosDB.rejected, (state) => {
        state.fileDatasetLoading = false;
        state.fileDataset = null;
    });


    builder.addCase(GetTrainingModelDetails.pending, (state) => {
      state.trainingModelDetailsLoading = true;
    });
    builder.addCase(GetTrainingModelDetails.fulfilled, (state, action) => {
      state.trainingModelDetailsLoading = false;
      if (action.payload?.success) {
        state.trainingModelDetails = action.payload.result;
      }
    });
    builder.addCase(GetTrainingModelDetails.rejected, (state) => {
      state.trainingModelDetailsLoading = false;
    });

    builder.addCase(DeleteTrainingModel.pending, (state) => {
      state.deletingTrainingModel = true;
    });
    builder.addCase(DeleteTrainingModel.fulfilled, (state) => {
      state.deletingTrainingModel = false;
      state.deleteTraingModelDialogOpen = false;
      state.deleteTrainingModelBodyParams = null;
      state.refreshSelectedMLCreatorTable++;
      state.refreshMLCreatorTable++;
    });
    builder.addCase(DeleteTrainingModel.rejected, (state) => {
      state.deletingTrainingModel = false;
      state.deleteTraingModelDialogOpen = false;
      state.deleteTrainingModelBodyParams = null;
      state.refreshSelectedMLCreatorTable++;
    });

    builder.addCase(AIOrchestratorInitTrain.pending, (state) => {
      state.aiInitiateTraining = true;
    });
    builder.addCase(AIOrchestratorInitTrain.fulfilled, (state, action) => {
      state.aiInitiateTraining = false;
      if (action.payload?.success) {
        state.columns = [];
        state.selectedColumn = null;
        state.selectedInputColumn = null;
        state.selectedOutputColumn = null;
        state.inputColumns = [];
        state.outputColumns = [];
        state.trainNewModelDialogOpen = false;
        state.refreshSelectedMLCreatorTable++;
      }
    });
    builder.addCase(AIOrchestratorInitTrain.rejected, (state) => {
      state.aiInitiateTraining = false;
    });

    builder.addCase(GetModelAlgorithmDetails.pending, (state) => {
      state.trainNewModelAlgorithmDialogLoading = true;
    });
    builder.addCase(GetModelAlgorithmDetails.fulfilled, (state, action) => {
      state.trainNewModelAlgorithmDialogLoading = false;
      if (action.payload?.success) {
        state.trainNewModelAlgorithmDialogOpen = false;
        state.recommendedModelType = null;
        state.modelAlgorithmList = [];
      }
    });
    builder.addCase(GetModelAlgorithmDetails.rejected, (state) => {
      state.trainNewModelAlgorithmDialogLoading = false;
    });
  },
});

// Export actions for dispatching
export const {
  handleToggleImportTrainingDataDialog,
  handleSetChosenFile,
  handleSetChosenName,
  handleToggleDeleteFilePopup,
  handleToggleFileDetailsPopup,
  handleToggleFileDatasetPopup,
  handleCreatorRowClick,
  handleModelRowClick,
  handleTrainNewModel,
  handleTrainNewModelAlgorithmList,
  handleSelectColumn,
  handleSelectInputColumn,
  handleSelectOutputColumn,
  handleMoveColumnToColumnsLeft,
  handleMoveColumnToColumnsRight,
  handleMoveColumnToInputLeft,
  handleMoveColumnToOutputRight,
  handleMoveAllToInputColumns,
  handleMoveAllToOutputColumns,
  handleMoveAllToColumnsFromInput,
  handleMoveAllToColumnsFromOutput,
  handleCloseSuccessfulTrainingDetails,
  handleToggleTrainingModelDetailsDialog,
  handleToggleDeleteTrainingModel,
  handleToggleFailedTrainingModal,
  handleRefreshMLCreatorTable,
  handleRefreshSelectedMLCreatorTable,
} = mlCreatorSlice.actions;

// Selector to access ML creator slice state
export const mlCreatorSelector = (state: RootState) => state.mlcreator;
